// features/schedule/components/EkipMemberList.tsx
//
// Ô điều khiển ekip nhiều người: hiện người của một vai thành các chip gỡ được,
// kèm ô chọn "＋ Thêm người". Dùng chung bởi tab Ekip của màn Đơn và ô chi tiết
// của màn Lịch.
//
// Mỗi lần sửa gửi đúng MỘT thao tác (thêm người X / gỡ người Y), không gửi lại
// cả danh sách — xem lời bình đầu useEkipAssign.ts (quyết định 0121). `members`
// ở đây chỉ để VẼ; nó không bao giờ được gửi ngược lên máy chủ.
import { StaffAvatar } from "@/components/StaffAvatar";
import type { EkipMember } from "../useEkipAssign";
import { EkipPicker, type EkipPickExtra } from "./EkipPicker";

// initialMembers chuẩn hoá mảng nhiều người của một vai trò trên order/schedule
// row — 6 cột phẳng cũ (photographerId/Name, makeupId/Name, retoucher/Name) đã
// bỏ khỏi OpenAPI, mảng EkipMember[] giờ là nguồn duy nhất.
export function initialMembers(members: EkipMember[] | undefined): EkipMember[] {
  return (members ?? []).map((m) => ({ id: m.id, name: m.name }));
}

export function EkipMemberList({
  label,
  members,
  department,
  branch,
  brand,
  canEdit,
  disabled,
  orderId,
  lockedReason,
  onAdd,
  onRemove,
}: {
  label: string;
  members: EkipMember[];
  department: string | string[];
  branch?: string | null;
  /** Thương hiệu của đơn — chỉ để xếp nhóm thợ cùng thương hiệu lên đầu. */
  brand?: string | null | undefined;
  canEdit: boolean;
  disabled: boolean;
  /** Đơn đang gán → ô chọn nhận phán quyết chứng chỉ theo đơn (QĐ 0101). */
  orderId?: string | undefined;
  /**
   * Có giá trị = vai này KHÔNG nhận thêm người trên đơn này, và đây là lý do
   * nói thẳng cho người dùng (QĐ 0199 — gói không kèm trang điểm).
   *
   * Khoá bằng cách BỎ ô chọn chứ không disable nó: một ô chọn xám không nói
   * được vì sao, mà lý do mới là thứ chỉ đường đi tiếp. Nút gỡ "×" của những
   * người đã có VẪN SỐNG — khoá cả đường gỡ là nhốt luôn người bị gán nhầm.
   */
  lockedReason?: string | undefined;
  /** Thêm ĐÚNG một người. Không nhận danh sách — xem lời bình đầu tệp. */
  onAdd: (person: EkipMember, extra?: EkipPickExtra) => void;
  /** Gỡ ĐÚNG một người. */
  onRemove: (person: EkipMember) => void;
}) {
  const selectedIds = members.map((m) => m.id).filter(Boolean);

  const pickMember = (id: string | null, name: string | null, extra?: EkipPickExtra) => {
    if (!id) return;
    // Đã có trong danh sách đang vẽ → khỏi gửi. Máy chủ cũng idempotent, nên
    // đây chỉ là tiết kiệm một lượt đi về, không phải chốt chặn.
    if (members.some((m) => m.id === id)) return;
    onAdd({ id, name: name ?? "" }, extra);
  };

  return (
    <div className="min-w-0 space-y-2">
      {label && <div className="text-xs font-medium text-muted-foreground">{label}</div>}
      {members.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {members.map((m) => (
            <span
              key={m.id || m.name}
              className="inline-flex max-w-full min-w-0 items-center gap-1.5 rounded-full border bg-surface py-1 pl-1.5 pr-2.5 text-sm"
            >
              <StaffAvatar staffId={m.id} name={m.name} sizePx={24} />
              <span className="min-w-0 truncate" title={m.name || m.id}>{m.name || m.id}</span>
              {canEdit && (
                // Vùng chạm nới bằng padding (min-h bị globals.css đè trên mobile),
                // -m âm để chip không phình theo.
                <button
                  type="button"
                  className="-my-1.5 -mr-1.5 shrink-0 rounded-full px-2 py-1.5 text-base leading-none text-muted-foreground hover:text-red-600"
                  disabled={disabled}
                  onClick={() => onRemove(m)}
                  aria-label={`Gỡ ${m.name}`}
                >
                  ×
                </button>
              )}
            </span>
          ))}
        </div>
      )}
      {canEdit && lockedReason && (
        <div
          className="rounded-md border px-2 py-1.5 text-xs"
          style={{
            background: "var(--color-warning-bg)",
            borderColor: "var(--color-warning)",
            color: "var(--color-warning-text)",
          }}
        >
          {lockedReason}
        </div>
      )}
      {canEdit && !lockedReason && (
        <EkipPicker
          label=""
          value={null}
          department={department}
          branch={branch}
          brand={brand}
          disabled={disabled}
          excludeIds={selectedIds}
          orderId={orderId}
          placeholder="＋ Thêm người"
          onChange={pickMember}
        />
      )}
      {!canEdit && members.length === 0 && (
        <div className="text-xs text-muted-foreground">Chưa phân công.</div>
      )}
    </div>
  );
}
